import { Link, useLocation } from "react-router-dom";
import { Home, Trophy, User, PlusCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "./ui/button";

export function Navbar() {
  const location = useLocation();
  const { isAuthenticated, login, logout, user } = useAuth();

  const navItems = [
    { to: "/", label: "Home", icon: Home },
    { to: "/challenges", label: "Challenges", icon: Trophy },
    { to: "/create-challenge", label: "Create", icon: PlusCircle },
    { to: "/profile", label: "Profile", icon: User },
  ];

  return (
    <>
      {/* Top Navigation Bar - Desktop */}
      <nav className="sticky top-0 z-50 bg-background-light dark:bg-background-dark border-b border-secondary-light dark:border-secondary-dark md:block hidden">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center space-x-1">
              <img
                src="/Dasher.png"
                alt="Dasher Logo"
                className="h-10 w-auto object-contain"
              />
              <span className="text-xl font-bold text-text-light dark:text-text-dark">
                Dasher
              </span>
            </Link>

            <div className="flex items-center space-x-6">
              {navItems.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={cn(
                      "flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors",
                      location.pathname === item.to
                        ? "text-primary bg-primary/10"
                        : "text-content-light dark:text-content-dark hover:text-primary hover:bg-secondary-light dark:hover:bg-secondary-dark"
                    )}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </div>

            <div>
              {isAuthenticated ? (
                <div className="flex items-center space-x-4">
                  <span className="text-sm text-content-light dark:text-content-dark">
                    {user?.email}
                  </span>
                  <Button variant="outline" size="sm" onClick={logout}>
                    Logout
                  </Button>
                </div>
              ) : (
                <Button size="sm" onClick={login}>
                  Login
                </Button>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Bottom Navigation Bar - Mobile */}
      <nav
        aria-label="Mobile navigation"
        className="fixed bottom-0 left-0 right-0 z-50 md:hidden"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        {/* HTML div for backdrop blur effect with clip-path */}
        <div
          className="absolute inset-0 bg-background-light/80 dark:bg-background-dark/80 backdrop-blur-md border-t border-secondary-light dark:border-secondary-dark"
          style={{
            clipPath: "polygon(0% 0%, 38% 0%, 40% 0%, 41.5% 3%, 43% 10%, 44% 18%, 45.5% 26%, 47% 32%, 48.5% 35%, 50% 36%, 51.5% 35%, 53% 32%, 54.5% 26%, 56% 18%, 57% 10%, 58.5% 3%, 60% 0%, 62% 0%, 100% 0%, 100% 100%, 0% 100%)"
          }}
        />

        {/* FAB Button - positioned absolutely in the notch */}
        <Link
          to="/create-challenge"
          className="absolute left-1/2 -translate-x-1/2 -top-4 z-20 flex items-center justify-center w-12 h-12 rounded-full bg-primary active:scale-95 transition-transform"
          aria-label="Create challenge"
        >
          <span
            className="material-symbols-outlined icon-bold text-background-dark text-2xl"
            aria-hidden="true"
          >
            add
          </span>
        </Link>

        {/* Navigation items */}
        <div className="relative z-10 mx-auto flex h-16 max-w-md items-center justify-evenly">
          {/* Home */}
          <Link
            to="/"
            className={cn(
              "flex flex-col items-center justify-center gap-1 min-w-11 min-h-11",
              location.pathname === "/"
                ? "text-primary"
                : "text-text-light dark:text-text-dark"
            )}
          >
            <span className="material-symbols-outlined" aria-hidden="true">
              home
            </span>
            <span
              className={cn(
                "text-xs",
                location.pathname === "/" ? "font-bold" : "font-medium"
              )}
            >
              Home
            </span>
          </Link>

          {/* Discover */}
          <Link
            to="/challenges"
            className={cn(
              "flex flex-col items-center justify-center gap-1 min-w-11 min-h-11",
              location.pathname === "/challenges"
                ? "text-primary"
                : "text-text-light dark:text-text-dark"
            )}
          >
            <span className="material-symbols-outlined" aria-hidden="true">
              explore
            </span>
            <span
              className={cn(
                "text-xs",
                location.pathname === "/challenges"
                  ? "font-bold"
                  : "font-medium"
              )}
            >
              Discover
            </span>
          </Link>

          {/* Spacer for FAB */}
          <div className="min-w-11 min-h-11" />

          {/* Challenges */}
          <Link
            to="/my-challenges"
            className={cn(
              "flex flex-col items-center justify-center gap-1 min-w-11 min-h-11",
              location.pathname === "/my-challenges"
                ? "text-primary"
                : "text-text-light dark:text-text-dark"
            )}
          >
            <span className="material-symbols-outlined" aria-hidden="true">
              emoji_events
            </span>
            <span
              className={cn(
                "text-xs",
                location.pathname === "/my-challenges"
                  ? "font-bold"
                  : "font-medium"
              )}
            >
              Challenges
            </span>
          </Link>

          {/* Profile */}
          <Link
            to="/profile"
            className={cn(
              "flex flex-col items-center justify-center gap-1 min-w-11 min-h-11",
              location.pathname === "/profile"
                ? "text-primary"
                : "text-text-light dark:text-text-dark"
            )}
          >
            <span className="material-symbols-outlined" aria-hidden="true">
              person
            </span>
            <span
              className={cn(
                "text-xs",
                location.pathname === "/profile" ? "font-bold" : "font-medium"
              )}
            >
              Profile
            </span>
          </Link>
        </div>
      </nav>
    </>
  );
}
